import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { Language, translations, categoryTranslations, subcategoryTranslations, productTranslations, ingredientTranslations, bowlStepTranslations, selectionLabelTranslations, CRC_TO_USD_RATE, LANGUAGES, CURRENCIES, COUNTRY_TO_CURRENCY, CurrencyInfo } from '../data/translations';
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  translateCategory: (category: string) => string;
  translateSubcategory: (subcategory: string) => string;
  translateProductName: (productId: string, fallback: string) => string;
  translateProductDescription: (productId: string, fallback?: string) => string | undefined;
  translateIngredient: (ingredientId: string, fallback: string) => string;
  translateBowlStep: (stepId: string) => {
    title: string;
    subtitle: string;
    note?: string;
  };
  translateSelectionLabel: (key: string) => string;
  formatPrice: (priceCRC: number) => {
    primary: string;
    secondary: string;
  };
  formatPriceInline: (priceCRC: number) => string;
  isSpanish: boolean;
  detectedCurrency: CurrencyInfo;
  detectedCountry: string;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
function formatCurrencyValue(amount: number, currency: CurrencyInfo): string {
  const formatted = currency.decimals === 0 ? Math.round(amount).toLocaleString() : amount.toFixed(currency.decimals);
  if (currency.position === 'after') {
    return `${formatted}${currency.symbol}`;
  }
  return `${currency.symbol}${formatted}`;
}
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguage] = useState<Language>('es');
  const [detectedCountry, setDetectedCountry] = useState<string>('CR');
  const [detectedCurrencyCode, setDetectedCurrencyCode] = useState<string>('CRC');
  // Detect user's country via IP on mount
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(5000)
        });
        if (!response.ok) throw new Error('API error');
        const data = await response.json();
        const countryCode = data.country_code || 'CR';
        setDetectedCountry(countryCode);
        const currencyCode = COUNTRY_TO_CURRENCY[countryCode] || 'USD';
        setDetectedCurrencyCode(currencyCode);
        // Auto-set language based on country if user hasn't changed it
        const countryToLang: Record<string, Language> = {
          CR: 'es',
          MX: 'es',
          AR: 'es',
          CO: 'es',
          CL: 'es',
          PE: 'es',
          ES: 'es',
          GT: 'es',
          HN: 'es',
          NI: 'es',
          DO: 'es',
          SV: 'es',
          EC: 'es',
          PA: 'es',
          US: 'en',
          CA: 'en',
          GB: 'en',
          AU: 'en',
          NZ: 'en',
          IE: 'en',
          FR: 'fr',
          BE: 'fr',
          CN: 'zh',
          TW: 'zh',
          BR: 'pt',
          PT: 'pt',
          DE: 'de',
          AT: 'de',
          CH: 'de'
        };
        const suggestedLang = countryToLang[countryCode];
        if (suggestedLang) {
          setLanguage(suggestedLang);
        }
      } catch {
        // Silently fail — default to CR/CRC/es
      }
    };
    detectCountry();
  }, []);
  const detectedCurrency = CURRENCIES[detectedCurrencyCode] || CURRENCIES.USD;
  const isSpanish = language === 'es';
  const t = useCallback((key: string): string => {
    const trans = translations[language];
    return (trans as any)[key] || key;
  }, [language]);
  const translateCategory = useCallback((category: string): string => {
    return categoryTranslations[language]?.[category] || category;
  }, [language]);
  const translateSubcategory = useCallback((subcategory: string): string => {
    return subcategoryTranslations[language]?.[subcategory] || subcategory;
  }, [language]);
  const translateProductName = useCallback((productId: string, fallback: string): string => {
    return productTranslations[language]?.[productId]?.name || fallback;
  }, [language]);
  const translateProductDescription = useCallback((productId: string, fallback?: string): string | undefined => {
    return productTranslations[language]?.[productId]?.description ?? fallback;
  }, [language]);
  const translateIngredient = useCallback((ingredientId: string, fallback: string): string => {
    return ingredientTranslations[language]?.[ingredientId] || fallback;
  }, [language]);
  const translateBowlStep = useCallback((stepId: string): {
    title: string;
    subtitle: string;
    note?: string;
  } => {
    return bowlStepTranslations[language]?.[stepId] || {
      title: stepId,
      subtitle: ''
    };
  }, [language]);
  const translateSelectionLabel = useCallback((key: string): string => {
    return selectionLabelTranslations[language]?.[key] || key;
  }, [language]);
  const formatPrice = useCallback((priceCRC: number): {
    primary: string;
    secondary: string;
  } => {
    const usd = priceCRC / CRC_TO_USD_RATE;
    const crcFormatted = formatCurrencyValue(priceCRC, CURRENCIES.CRC);
    // If user is in Costa Rica, show CRC primary, USD secondary
    if (detectedCurrencyCode === 'CRC') {
      const usdFormatted = formatCurrencyValue(usd, CURRENCIES.USD);
      return {
        primary: crcFormatted,
        secondary: usdFormatted
      };
    }
    // If user is in the US or a USD country, show USD primary, CRC secondary
    if (detectedCurrencyCode === 'USD') {
      const usdFormatted = formatCurrencyValue(usd, CURRENCIES.USD);
      return {
        primary: usdFormatted,
        secondary: crcFormatted
      };
    }
    // For any other country: show USD primary, local currency secondary
    const usdFormatted = formatCurrencyValue(usd, CURRENCIES.USD);
    const localAmount = usd * detectedCurrency.rateFromUSD;
    const localFormatted = formatCurrencyValue(localAmount, detectedCurrency);
    return {
      primary: usdFormatted,
      secondary: `≈ ${localFormatted}`
    };
  }, [detectedCurrencyCode, detectedCurrency]);
  const formatPriceInline = useCallback((priceCRC: number): string => {
    const usd = priceCRC / CRC_TO_USD_RATE;
    const crcFormatted = formatCurrencyValue(priceCRC, CURRENCIES.CRC);
    if (detectedCurrencyCode === 'CRC') {
      const usdFormatted = formatCurrencyValue(usd, CURRENCIES.USD);
      return `${crcFormatted} (${usdFormatted})`;
    }
    if (detectedCurrencyCode === 'USD') {
      const usdFormatted = formatCurrencyValue(usd, CURRENCIES.USD);
      return `${usdFormatted} (${crcFormatted})`;
    }
    // Other countries: show USD + local
    const usdFormatted = formatCurrencyValue(usd, CURRENCIES.USD);
    const localAmount = usd * detectedCurrency.rateFromUSD;
    const localFormatted = formatCurrencyValue(localAmount, detectedCurrency);
    return `${usdFormatted} (≈ ${localFormatted})`;
  }, [detectedCurrencyCode, detectedCurrency]);
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t,
    translateCategory,
    translateSubcategory,
    translateProductName,
    translateProductDescription,
    translateIngredient,
    translateBowlStep,
    translateSelectionLabel,
    formatPrice,
    formatPriceInline,
    isSpanish,
    detectedCurrency,
    detectedCountry
  }} data-id="element-1297">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}